class VideoGallery {
  constructor(container) {
    this.container = container;
    this.videos = container.querySelectorAll('.gallery-video');
    this.controls = {};
    this.pipSupported = document.pictureInPictureEnabled;
    this.grid = container.querySelector('.video-grid');
    this.leftArrow = container.querySelector('.scroll-left');
    this.rightArrow = container.querySelector('.scroll-right');
    this.currentlyPlaying = null;
    
    this.init();
    this.initScrollControls();
  }
  
  init() {
    this.videos.forEach((video, index) => {
      const container = video.closest('.video-container');
      const controls = this.getControls(container);
      
      this.controls[video.dataset.videoId] = {
        video,
        container,
        controls,
        isPlaying: false,
        isMuted: false,
        isPipActive: false
      };
      
      video.muted = false;
      controls.muteBtn.querySelector('.volume-on').style.display = 'block';
      controls.muteBtn.querySelector('.volume-off').style.display = 'none';
      controls.playOverlay.style.display = 'flex';
      
      video.addEventListener('loadedmetadata', () => {
        controls.duration.textContent = this.formatTime(video.duration);
      });
      
      this.bindEvents(video, controls, index);
      
      if (!this.pipSupported) {
        controls.pipTriggerBtn.style.display = 'none';
        controls.pipTriggerBtn.disabled = true;
      }
    });
    
    document.addEventListener('enterpictureinpicture', this.onPipChange.bind(this));
    document.addEventListener('leavepictureinpicture', this.onPipChange.bind(this));
  }
  
  initScrollControls() {
    if (!this.grid) return;
    
    this.checkArrows();
    
    if (this.leftArrow) {
      this.leftArrow.addEventListener('click', () => {
        const scrollAmount = this.grid.clientWidth * 0.8;
        this.grid.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
    }
    
    if (this.rightArrow) {
      this.rightArrow.addEventListener('click', () => {
        const scrollAmount = this.grid.clientWidth * 0.8;
        this.grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }
    
    this.grid.addEventListener('scroll', this.checkArrows.bind(this));
    window.addEventListener('resize', this.checkArrows.bind(this));
    
    this.grid.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (this.leftArrow) this.leftArrow.click();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (this.rightArrow) this.rightArrow.click();
      }
    });
  }
  
  checkArrows() {
    if (!this.grid) return;
    
    const scrollLeft = this.grid.scrollLeft;
    const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
    
    if (this.leftArrow) {
      if (scrollLeft > 10) {
        this.leftArrow.classList.add('visible');
      } else {
        this.leftArrow.classList.remove('visible');
      }
    }
    
    if (this.rightArrow) {
      if (scrollLeft < maxScroll - 10) {
        this.rightArrow.classList.add('visible');
      } else {
        this.rightArrow.classList.remove('visible');
      }
    }
  }
  
  getControls(container) {
    return {
      playOverlay: container.querySelector('.play-overlay'),
      pipTriggerBtn: container.querySelector('.pip-trigger-btn'),
      muteBtn: container.querySelector('.mute-btn'),
      progressBar: container.querySelector('.progress-bar'),
      progressFill: container.querySelector('.progress-fill'),
      duration: container.querySelector('.video-duration'),
      volumeOn: container.querySelector('.volume-on'),
      volumeOff: container.querySelector('.volume-off')
    };
  }
  
  bindEvents(video, controls, index) {
    const state = this.controls[video.dataset.videoId];
    const container = video.closest('.video-container');
    
    const triggerPip = (e) => {
      e.stopPropagation();
      this.startPictureInPicture(video, controls, state);
    };
    
    video.addEventListener('click', triggerPip);
    controls.playOverlay.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startPictureInPicture(video, controls, state);
    });
    controls.pipTriggerBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startPictureInPicture(video, controls, state);
    });
    
    controls.muteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMute(video, controls, state);
    });
    
    video.addEventListener('timeupdate', () => {
      this.updateProgress(video, controls);
    });
    
    controls.progressBar.addEventListener('click', (e) => {
      e.stopPropagation();
      const rect = controls.progressBar.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      video.currentTime = percent * video.duration;
    });
    
    video.addEventListener('ended', () => {
      state.isPlaying = false;
      controls.playOverlay.style.display = 'flex';
      container.classList.remove('playing');
      if (this.currentlyPlaying === video) {
        this.currentlyPlaying = null;
      }
    });
    
    video.addEventListener('play', () => {
      state.isPlaying = true;
      controls.playOverlay.style.display = 'none';
      container.classList.add('playing');
      this.currentlyPlaying = video;
    });
    
    video.addEventListener('pause', () => {
      state.isPlaying = false;
      container.classList.remove('playing');
      if (!state.isPipActive) {
        controls.playOverlay.style.display = 'flex';
      }
      if (this.currentlyPlaying === video) {
        this.currentlyPlaying = null;
      }
    });
    
    container.addEventListener('mouseenter', () => {
      container.classList.add('controls-visible');
    });
    container.addEventListener('mouseleave', () => {
      container.classList.remove('controls-visible');
    });
    
    let touchTimer;
    container.addEventListener('touchstart', () => {
      container.classList.toggle('controls-visible');
      clearTimeout(touchTimer);
      touchTimer = setTimeout(() => {
        container.classList.remove('controls-visible');
      }, 3000);
    });
  }
  
  startPictureInPicture(video, controls, state) {
    this.stopAllVideos(video);
    
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(err => {
        console.warn('Failed to exit PIP:', err);
      });
      return;
    }
    
    if (!this.pipSupported) {
      console.warn('Picture-in-Picture is not supported in this browser');
      this.playInContainer(video, controls, state);
      return;
    }
    
    video.requestPictureInPicture()
      .then(() => {
        state.isPipActive = true;
        video.play().catch(err => {
          console.warn('Failed to play video in PIP:', err);
        });
        controls.playOverlay.style.display = 'none';
        state.container.classList.add('pip-active');
      })
      .catch(err => {
        console.warn('Failed to enter PIP mode:', err);
        this.playInContainer(video, controls, state);
      });
  }
  
  stopAllVideos(excludeVideo) {
    this.videos.forEach(video => {
      if (video !== excludeVideo && !video.paused) {
        video.pause();
        const state = this.controls[video.dataset.videoId];
        if (state) {
          state.isPlaying = false;
          state.container.classList.remove('playing');
          if (!state.isPipActive) {
            state.controls.playOverlay.style.display = 'flex';
          }
        }
      }
    });
    
    if (this.currentlyPlaying && this.currentlyPlaying !== excludeVideo) {
      this.currentlyPlaying = null;
    }
  }
  
  playInContainer(video, controls, state) {
    this.stopAllVideos(video);
    
    video.play()
      .then(() => {
        controls.playOverlay.style.display = 'none';
        state.container.classList.add('playing');
        this.currentlyPlaying = video;
      })
      .catch(err => {
        console.warn('Failed to play video:', err);
        controls.playOverlay.style.display = 'flex';
      });
  }
  
  toggleMute(video, controls, state) {
    video.muted = !video.muted;
    state.isMuted = video.muted;
    
    if (video.muted) {
      controls.volumeOn.style.display = 'none';
      controls.volumeOff.style.display = 'block';
    } else {
      controls.volumeOn.style.display = 'block';
      controls.volumeOff.style.display = 'none';
    }
  }
  
  onPipChange(event) {
    this.videos.forEach(video => {
      const state = this.controls[video.dataset.videoId];
      if (!state) return;
      
      const isPip = document.pictureInPictureElement === video;
      state.isPipActive = isPip;
      
      if (isPip) {
        state.container.classList.add('pip-active');
        state.controls.playOverlay.style.display = 'none';
      } else {
        state.container.classList.remove('pip-active');
        if (video.paused) {
          state.controls.playOverlay.style.display = 'flex';
        }
      }
    });
  }
  
  updateProgress(video, controls) {
    if (video.duration) {
      const percent = (video.currentTime / video.duration) * 100;
      controls.progressFill.style.width = `${percent}%`;
      
      if (controls.duration) {
        const current = this.formatTime(video.currentTime);
        const total = this.formatTime(video.duration);
        controls.duration.textContent = `${current} / ${total}`;
      }
    }
  }
  
  formatTime(seconds) {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('.video-gallery-outside');
  galleries.forEach(gallery => {
    new VideoGallery(gallery);
  });
});